'use client';

import { useEffect, useState } from 'react';
import { X, ArrowUp, ArrowDown } from 'lucide-react';
import { usePathname } from '@/i18n/routing';

function isIOS() {
  return (
    /iPhone|iPad|iPod/.test(navigator.userAgent) ||
    // iPadOS 13+ reports as desktop Safari; touch points give it away.
    (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1)
  );
}
function isStandalone() {
  return (
    window.matchMedia('(display-mode: standalone)').matches ||
    (window.navigator as unknown as { standalone?: boolean }).standalone === true
  );
}
function isIOSChrome() {
  return /CriOS/.test(navigator.userAgent);
}

// iOS share glyph (square with an up-arrow) — the icon users actually tap.
function ShareGlyph() {
  return (
    <svg viewBox="0 0 24 24" width="16" height="16" fill="none" className="inline-block align-text-bottom">
      <path
        d="M12 15V4m0 0L8.5 7.5M12 4l3.5 3.5"
        stroke="#0a84ff"
        strokeWidth="1.8"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <path
        d="M7 11H6a2 2 0 0 0-2 2v6a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2v-6a2 2 0 0 0-2-2h-1"
        stroke="#0a84ff"
        strokeWidth="1.8"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

const B = ({ children }: { children: React.ReactNode }) => (
  <b className="text-zinc-800 dark:text-zinc-100">{children}</b>
);

// Auto-appearing, spring-in popup that walks iOS visitors through adding wow to
// the home screen (the only path to full-screen + push on iOS). Shows once for
// not-yet-installed iOS users; hidden on desktop / once installed / after
// dismissal. Steps adapt to Safari vs iOS Chrome.
export default function InstallGuideCard() {
  const pathname = usePathname();
  const [open, setOpen] = useState(false);
  const [chrome, setChrome] = useState(false);
  useEffect(() => {
    // Staff live in /admin and don't need the app on their home screen.
    if (pathname.startsWith('/admin')) return;
    // No persisted dismissal: the guide reappears on every load until the user
    // actually installs (standalone), so a stray close never hides it for good.
    if (!isIOS() || isStandalone()) return;
    setChrome(isIOSChrome());
    const t = setTimeout(() => setOpen(true), 500);
    return () => clearTimeout(t);
  }, [pathname]);

  function dismiss() {
    setOpen(false);
  }

  if (!open) return null;

  const steps: React.ReactNode[] = chrome
    ? [
        <>
          點<B>右上角</B>的分享按鈕 <ShareGlyph />
        </>,
        <>
          選單往下滑,點「<B>加入主畫面</B>」
        </>,
      ]
    : [
        <>
          點<B>右下角</B>的「<B>⋯</B>」
        </>,
        <>
          點「<B>分享</B>」 <ShareGlyph />
        </>,
        <>
          往下滑,點「<B>加入主畫面</B>」
        </>,
      ];

  // Float near the share button instead of covering the screen: Chrome's share
  // is top-right (card in the upper third), Safari's is bottom-right (lower
  // third). No scrim — the login form behind stays visible and usable.
  const posClass = chrome ? 'top-[8vh]' : 'bottom-[8vh]';
  const originClass = chrome ? 'origin-top' : 'origin-bottom';

  return (
    <>
      {/* animated arrow pointing at the browser's share button */}
      <div
        className={`pointer-events-none fixed right-3 z-[71] flex h-10 w-10 items-center justify-center rounded-full bg-brand text-white shadow-lg shadow-brand/40 ${
          chrome ? 'top-2 animate-a2hs-bob-up' : 'bottom-2 animate-a2hs-bob-down'
        }`}
      >
        {chrome ? <ArrowUp size={20} strokeWidth={2.5} /> : <ArrowDown size={20} strokeWidth={2.5} />}
      </div>

      <div className={`pointer-events-none fixed inset-x-0 z-[70] flex justify-center px-5 ${posClass}`}>
        <div
          className={`animate-a2hs-pop pointer-events-auto relative w-full max-w-[20rem] rounded-3xl border border-zinc-200/70 bg-white p-5 shadow-2xl dark:border-zinc-700/60 dark:bg-zinc-900 ${originClass}`}
        >
        {/* caret pointing toward the browser's share button */}
        {chrome ? (
          <span className="absolute -top-1.5 right-10 h-3 w-3 rotate-45 border-l border-t border-zinc-200/70 bg-white dark:border-zinc-700/60 dark:bg-zinc-900" />
        ) : (
          <span className="absolute -bottom-1.5 right-10 h-3 w-3 rotate-45 border-b border-r border-zinc-200/70 bg-white dark:border-zinc-700/60 dark:bg-zinc-900" />
        )}
        <button
          onClick={dismiss}
          aria-label="關閉"
          className="absolute right-3.5 top-3.5 flex h-8 w-8 items-center justify-center rounded-full bg-zinc-100 text-zinc-500 dark:bg-zinc-800"
        >
          <X size={16} />
        </button>

        <div className="flex flex-col items-center pt-1 text-center">
          <div className="flex h-16 w-16 items-center justify-center rounded-[1.25rem] bg-gradient-to-br from-emerald-500 to-teal-500 text-4xl font-extrabold text-white shadow-lg shadow-emerald-500/30">
            w
          </div>
          <p className="mt-3 text-lg font-bold leading-tight">把 wow 加到主畫面</p>
          <p className="mt-1 text-xs text-zinc-500">全螢幕開啟 · 接收專欄與行情推播</p>
        </div>

        <ol className="mt-4 space-y-2.5">
          {steps.map((s, i) => (
            <li key={i} className="flex items-start gap-2.5 text-sm">
              <span className="mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-brand text-[11px] font-bold text-white">
                {i + 1}
              </span>
              <span className="leading-relaxed text-zinc-600 dark:text-zinc-300">{s}</span>
            </li>
          ))}
        </ol>

        {chrome && (
          <p className="mt-3 rounded-lg bg-amber-50 px-3 py-2 text-[11px] leading-relaxed text-amber-700 dark:bg-amber-950/30 dark:text-amber-300">
            建議改用 <b>Safari</b> 開啟本站再加入主畫面,推播通知最穩定。
          </p>
        )}

        <button
          onClick={dismiss}
          className="mt-4 w-full rounded-xl bg-gradient-to-r from-emerald-500 to-teal-500 py-2.5 text-sm font-semibold text-white shadow-md shadow-emerald-500/25 transition active:scale-[0.99]"
        >
          我知道了
        </button>
        </div>
      </div>
    </>
  );
}
